import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Calendar, ChevronDown, X, Sun, Sunrise, CalendarDays, PartyPopper, Clock } from 'lucide-react';
import { useTranslation } from '../i18n';

/**
 * Options de filtrage par date prédéfinies
 */
const DATE_OPTIONS = [
  { id: 'all', tKey: 'timing.all', icon: CalendarDays, color: '#3BADE5' },
  { id: 'today', tKey: 'timing.today', icon: Sun, color: '#22c55e' },
  { id: 'tomorrow', tKey: 'timing.tomorrow', icon: Sunrise, color: '#eab308' },
  { id: 'week', tKey: 'timing.thisWeek', icon: CalendarDays, color: '#8b5cf6' },
  { id: 'weekend', tKey: 'timing.thisWeekend', icon: PartyPopper, color: '#f97316' },
];

/**
 * Calcule si une date correspond au filtre sélectionné
 */
export function matchesDateFilter(sessionDate, filterId) {
  if (filterId === 'all') return true;
  
  const session = new Date(sessionDate);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  
  const sessionDay = new Date(session);
  sessionDay.setHours(0, 0, 0, 0);
  
  switch (filterId) {
    case 'today':
      return sessionDay.getTime() === today.getTime();
    
    case 'tomorrow':
      return sessionDay.getTime() === tomorrow.getTime();
    
    case 'week': {
      const endOfWeek = new Date(today);
      endOfWeek.setDate(today.getDate() + (7 - today.getDay()));
      return sessionDay >= today && sessionDay <= endOfWeek;
    }
    
    case 'weekend': {
      const dayOfWeek = sessionDay.getDay();
      // Samedi (6) ou Dimanche (0)
      const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
      // Dans les 14 prochains jours
      const twoWeeksLater = new Date(today);
      twoWeeksLater.setDate(today.getDate() + 14);
      return isWeekend && sessionDay >= today && sessionDay <= twoWeeksLater;
    }
    
    default:
      // Si c'est une date spécifique (YYYY-MM-DD)
      if (filterId.match(/^\d{4}-\d{2}-\d{2}$/)) {
        const filterDate = new Date(filterId);
        filterDate.setHours(0, 0, 0, 0);
        return sessionDay.getTime() === filterDate.getTime();
      }
      return true;
  }
}

/**
 * Compte les sessions par option de date
 */
export function getSessionCountsByDate(sessions) {
  const counts = {};
  DATE_OPTIONS.forEach(opt => {
    counts[opt.id] = sessions.filter(s => matchesDateFilter(s.date, opt.id)).length;
  });
  return counts;
}

/**
 * DateFilter - Filtre par date flottant pour la carte
 */
export default function DateFilter({
  selectedDate = 'all',
  onDateChange,
  counts = {},
  className = ''
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const { t } = useTranslation();
  const selectedOption = DATE_OPTIONS.find(o => o.id === selectedDate) || DATE_OPTIONS[0];

  return (
    <div className={`relative ${className}`} data-testid="date-filter">
      {/* Bouton principal */}
      <motion.button
        onClick={() => setIsExpanded(!isExpanded)}
        className={`
          flex items-center gap-2 px-4 py-2 rounded-full
          bg-white/95 backdrop-blur-sm shadow-lg border border-gray-200
          text-sm font-medium transition-all duration-200
          hover:shadow-xl
        `}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        data-testid="date-filter-toggle"
      >
        <Calendar className="w-4 h-4" style={{ color: selectedOption.color }} />
        <span className="text-gray-700">{t(selectedOption.tKey)}</span>
        {counts[selectedDate] > 0 && (
          <span 
            className="px-1.5 py-0.5 rounded-full text-xs font-bold text-white"
            style={{ backgroundColor: selectedOption.color }}
          >
            {counts[selectedDate]}
          </span>
        )}
        <ChevronDown 
          className={`w-4 h-4 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} 
        />
      </motion.button>

      {/* Menu déroulant */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            className="absolute top-full left-0 mt-2 w-56 bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden z-50"
          >
            <div className="p-2">
              <div className="text-xs font-medium text-gray-400 uppercase px-3 py-2">
                {t('timing.filterByDate')}
              </div>
              
              {DATE_OPTIONS.map((option) => {
                const Icon = option.icon;
                const isSelected = selectedDate === option.id;
                const count = counts[option.id] || 0;
                
                return (
                  <button
                    key={option.id}
                    onClick={() => {
                      onDateChange(option.id);
                      setIsExpanded(false);
                    }}
                    className={`
                      w-full flex items-center gap-3 px-3 py-2.5 rounded-lg
                      transition-all duration-150
                      ${isSelected 
                        ? 'bg-gray-100' 
                        : 'hover:bg-gray-50'
                      }
                    `}
                    data-testid={`date-filter-${option.id}`}
                  >
                    <div 
                      className={`
                        w-8 h-8 rounded-full flex items-center justify-center
                        ${isSelected ? 'bg-opacity-20' : 'bg-opacity-10'}
                      `}
                      style={{ backgroundColor: `${option.color}20` }}
                    >
                      <Icon className="w-4 h-4" style={{ color: option.color }} />
                    </div>
                    <span className={`flex-1 text-left ${isSelected ? 'font-semibold text-gray-900' : 'text-gray-700'}`}>
                      {t(option.tKey)}
                    </span>
                    {count > 0 && (
                      <span 
                        className={`
                          px-2 py-0.5 rounded-full text-xs font-bold
                          ${isSelected ? 'text-white' : 'text-gray-600 bg-gray-100'}
                        `}
                        style={isSelected ? { backgroundColor: option.color } : {}}
                      >
                        {count}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
            
            {/* Séparateur et option calendrier personnalisé */}
            <div className="border-t border-gray-100 p-2">
              <button
                onClick={() => {
                  // Pour l'instant, on ferme simplement
                  // Une future amélioration pourrait ouvrir un date picker
                  setIsExpanded(false);
                }}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-gray-50 text-gray-500"
              >
                <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                  <Calendar className="w-4 h-4 text-gray-400" />
                </div>
                <span className="text-sm">Choisir une date...</span>
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Overlay pour fermer le menu */}
      {isExpanded && (
        <div 
          className="fixed inset-0 z-40" 
          onClick={() => setIsExpanded(false)}
        />
      )}
    </div>
  );
}

/**
 * Composant compact pour affichage inline (pills)
 */
export function DateFilterPills({
  selectedDate = 'all',
  onDateChange,
  counts = {},
  className = ''
}) {
  return (
    <div className={`flex items-center gap-1 ${className}`} data-testid="date-filter-pills">
      {DATE_OPTIONS.slice(0, 4).map((option) => {
        const isSelected = selectedDate === option.id;
        const count = counts[option.id] || 0;
        
        return (
          <motion.button
            key={option.id}
            onClick={() => onDateChange(option.id)}
            className={`
              px-3 py-1.5 rounded-full text-xs font-medium
              transition-all duration-200 flex items-center gap-1.5
              ${isSelected 
                ? 'text-white shadow-md' 
                : 'bg-white/80 text-gray-600 hover:bg-white'
              }
            `}
            style={isSelected ? { backgroundColor: option.color } : {}}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            data-testid={`date-pill-${option.id}`}
          >
            <span>{option.name}</span>
            {count > 0 && (
              <span className={`${isSelected ? 'bg-white/30' : 'bg-gray-200'} px-1.5 rounded-full`}>
                {count}
              </span>
            )}
          </motion.button>
        );
      })}
    </div>
  );
}

/**
 * Badge indicateur pour "Aujourd'hui" ou "Demain"
 */
export function DateBadge({ date }) {
  const { t } = useTranslation();
  const sessionDate = new Date(date);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  
  const sessionDay = new Date(sessionDate);
  sessionDay.setHours(0, 0, 0, 0);
  
  if (sessionDay.getTime() === today.getTime()) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-500/20 text-green-600 text-xs font-semibold">
        <Sun className="w-3 h-3" />
        {t('timing.today')}
      </span>
    );
  }
  
  if (sessionDay.getTime() === tomorrow.getTime()) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-yellow-500/20 text-yellow-600 text-xs font-semibold">
        <Sunrise className="w-3 h-3" />
        {t('timing.tomorrow')}
      </span>
    );
  }
  
  return null;
}

/**
 * Badge "Commence dans X minutes/heures"
 * Affiche le temps restant avant le début de la session
 */
export function StartsInBadge({ date, startTime }) {
  const { t } = useTranslation();
  const now = new Date();
  
  // Parse session date and time
  const sessionDate = new Date(date);
  const [hours, minutes] = startTime.split(':').map(Number);
  sessionDate.setHours(hours, minutes, 0, 0);
  
  const diffMs = sessionDate.getTime() - now.getTime();
  
  // Only show if session is today and hasn't started yet
  if (diffMs <= 0) return null;
  
  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMinutes / 60);
  const remainingMinutes = diffMinutes % 60;
  
  // Only show for sessions starting within the next 6 hours
  if (diffHours >= 6) return null;
  
  let text = '';
  let urgencyClass = '';
  
  if (diffMinutes <= 30) {
    text = t('timing.inMinutes', { min: diffMinutes });
    urgencyClass = 'bg-red-500/20 text-red-600 animate-pulse';
  } else if (diffMinutes <= 60) {
    text = t('timing.inMinutes', { min: diffMinutes });
    urgencyClass = 'bg-orange-500/20 text-orange-600';
  } else if (diffHours === 1) {
    text = t('timing.inOneHour', { min: remainingMinutes > 0 ? remainingMinutes : '' });
    urgencyClass = 'bg-yellow-500/20 text-yellow-600';
  } else {
    text = t('timing.inHours', { hours: diffHours, min: remainingMinutes > 0 ? remainingMinutes : '' });
    urgencyClass = 'bg-blue-500/20 text-blue-600';
  }
  
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${urgencyClass}`}>
      <Clock className="w-3 h-3" />
      {text}
    </span>
  );
}

/**
 * Trie les sessions par heure de début (les plus proches en premier)
 * Sessions d'aujourd'hui non commencées en premier, puis par date/heure
 */
export function sortSessionsByTime(sessions) {
  const now = new Date();
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  return [...sessions].sort((a, b) => {
    // Parse dates and times
    const dateA = new Date(a.date);
    const dateB = new Date(b.date);
    
    const [hoursA, minsA] = (a.start_time || '00:00').split(':').map(Number);
    const [hoursB, minsB] = (b.start_time || '00:00').split(':').map(Number);
    
    const fullDateA = new Date(dateA);
    fullDateA.setHours(hoursA, minsA, 0, 0);
    
    const fullDateB = new Date(dateB);
    fullDateB.setHours(hoursB, minsB, 0, 0);
    
    // Check if sessions are today
    const dayA = new Date(dateA);
    dayA.setHours(0, 0, 0, 0);
    const dayB = new Date(dateB);
    dayB.setHours(0, 0, 0, 0);
    
    const isTodayA = dayA.getTime() === today.getTime();
    const isTodayB = dayB.getTime() === today.getTime();
    
    // Check if sessions haven't started yet
    const notStartedA = fullDateA.getTime() > now.getTime();
    const notStartedB = fullDateB.getTime() > now.getTime();
    
    // Priority: Today's upcoming sessions first
    if (isTodayA && notStartedA && !(isTodayB && notStartedB)) return -1;
    if (isTodayB && notStartedB && !(isTodayA && notStartedA)) return 1;
    
    // Then sort by full date/time
    return fullDateA.getTime() - fullDateB.getTime();
  });
}

export { DATE_OPTIONS };
